// src/lib/mfhSnapshot.ts
// MFH-Snapshot, gleiches Muster wie src/lib/etwSnapshot.ts (siehe dort fuer die
// Begruendung des fehlenden Cross-File-Imports). MFH hat zwei Eingabewege fuer
// die Fläche/Miete (mgmtMode): "gesamt" (zwei Summenfelder) oder "einheiten"
// (ein units[]-Array je Wohneinheit) -- beide werden im Snapshot aufbewahrt,
// unabhaengig davon, welcher Modus beim Speichern aktiv war, damit ein
// Moduswechsel nach dem Wiederoeffnen nicht die jeweils andere Eingabe verliert.

export type AnalyzerTypeId = "etw" | "mfh" | "efh" | "gewerbe" | "mixeduse";

/** confirmedFields: Phase-4 Runde 2b, Baustein 2 -- siehe etwSnapshot.ts fuer die
 *  ausfuehrliche Begruendung. Optional/abwaertskompatibel. */
export type DealSnapshotEnvelope<TInput, TPlayground> = {
  version: number;
  analyzerType: AnalyzerTypeId;
  input: TInput;
  playground: TPlayground;
  confirmedFields: string[];
};

function resolveField<T>(raw: Record<string, unknown> | undefined, key: string, fallback: T): T {
  const value = raw?.[key];
  if (value === undefined || value === null) return fallback;
  if (typeof fallback === "number") return typeof value === "number" && Number.isFinite(value) ? (value as T) : fallback;
  if (typeof fallback === "boolean") return typeof value === "boolean" ? (value as T) : fallback;
  if (typeof fallback === "string") return typeof value === "string" ? (value as T) : fallback;
  return (value as T) ?? fallback;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function resolveConfirmedFields(raw: Record<string, unknown> | undefined, key: string): string[] {
  const value = raw?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

function newUnitId(): string {
  const c: any = (globalThis as any).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `u-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export type MfhUnit = { id: string; name: string; areaM2: number; rentPerM2: number };

export const MFH_SNAPSHOT_VERSION = 1;

export type MfhSnapshotInput = {
  mode: "einfach" | "erweitert";
  mgmtMode: "gesamt" | "einheiten";
  gesamtFlaecheM2: number;
  kaltmieteJahr: number;
  units: MfhUnit[];
  nichtUmlagefaehigeKosten: number;
  leerstandPct: number;
  kaufpreis: number;
  nkRenovierung: number;
  nkSanierung: number;
  eigenkapital: number;
  manualLoan: boolean;
  darlehenManual: number;
  zins: number;
  tilgung: number;
  capexRuecklagePctBrutto: number;
  // Bewirtschaftung Pauschal/Einzeln (Baustein 5, Korrektur der frueheren "3 % uebernehmen"-
  // Vorgabe): null = kein Modus gespeichert (alter Snapshot vor diesem Feature ODER der
  // unveraenderte freie-Einstieg-Pfad) -- die eigentliche Migrations-Entscheidung (Pauschal
  // vs. Einzeln anhand des historischen capexRuecklagePctBrutto) passt NICHT hierher (kein
  // Zugriff auf grossRentYear/Flaeche in dieser Schicht), sondern in
  // core/score/mfhDealScore.ts' resolveMfhBewirtschaftung() -- exakt dasselbe Muster wie
  // legacyLtvPct bei der EK-Darlehensregel. verwaltung/instandhaltungJeM2Jahr nur im
  // Einzeln-Modus relevant, sonst 0. baujahr/hatAufzug sind reine Annahme-Eingaben fuer den
  // § 28 II. BV-Vorschlag (core/bewirtschaftung.ts), kein Pflichtfeld.
  bewirtschaftungModus: "pauschal" | "einzeln" | null;
  verwaltung: number;
  instandhaltungJeM2Jahr: number;
  baujahr: number | null;
  hatAufzug: boolean;
  mietSteigerung: number;
  kostenSteigerung: number;
  /** Deal-Score v2 Leerstand-Obergrenze. Alte Snapshots ohne dieses Feld gelten als "vollstaendig". */
  vermietet: "vollstaendig" | "teilweise" | "nein";
};

export type MfhSnapshotPlayground = {
  priceAdjPct: number;
  rentAdjPct: number;
  applyAdjustments: boolean;
};

export type MfhSnapshot = DealSnapshotEnvelope<MfhSnapshotInput, MfhSnapshotPlayground>;

// UI-Defaults, siehe MFHCheck.tsx (resetBeispiel/Beispielobjekt).
export const MFH_INPUT_DEFAULTS: MfhSnapshotInput = {
  mode: "einfach",
  mgmtMode: "gesamt",
  gesamtFlaecheM2: 520,
  kaltmieteJahr: 45_000,
  units: [
    { id: "default-we1", name: "WE 1", areaM2: 53, rentPerM2: 9.5 },
    { id: "default-we2", name: "WE 2", areaM2: 56, rentPerM2: 9.2 },
  ],
  nichtUmlagefaehigeKosten: 6_500,
  leerstandPct: 0.04,
  kaufpreis: 650_000,
  nkRenovierung: 0,
  nkSanierung: 0,
  eigenkapital: 225_000,
  manualLoan: false,
  darlehenManual: 400_000,
  zins: 0.035,
  tilgung: 0.02,
  capexRuecklagePctBrutto: 0.03,
  bewirtschaftungModus: null,
  verwaltung: 0,
  instandhaltungJeM2Jahr: 0,
  baujahr: null,
  hatAufzug: false,
  mietSteigerung: 0.01,
  kostenSteigerung: 0.015,
  vermietet: "vollstaendig",
};

export const MFH_PLAYGROUND_DEFAULTS: MfhSnapshotPlayground = {
  priceAdjPct: 0,
  rentAdjPct: 0,
  applyAdjustments: true,
};

export function buildMfhSnapshot(
  input: MfhSnapshotInput,
  playground: MfhSnapshotPlayground,
  confirmedFields: string[] = []
): MfhSnapshot {
  return { version: MFH_SNAPSHOT_VERSION, analyzerType: "mfh", input, playground, confirmedFields };
}

function resolveUnits(raw: unknown): MfhUnit[] {
  if (!Array.isArray(raw) || raw.length === 0) return MFH_INPUT_DEFAULTS.units;
  return raw.map((u, i) => {
    const r = asRecord(u);
    return {
      id: typeof r.id === "string" && r.id ? r.id : newUnitId(),
      name: typeof r.name === "string" && r.name ? r.name : `WE ${i + 1}`,
      areaM2: typeof r.areaM2 === "number" && Number.isFinite(r.areaM2) ? r.areaM2 : 0,
      rentPerM2: typeof r.rentPerM2 === "number" && Number.isFinite(r.rentPerM2) ? r.rentPerM2 : 0,
    };
  });
}

export type ResolvedMfhSnapshot = {
  input: MfhSnapshotInput;
  playground: MfhSnapshotPlayground;
  /** false, wenn Kaufpreis und die zum Modus passenden Flaechen-/Mietangaben fehlen. */
  complete: boolean;
  confirmedFields: string[];
};

export function resolveMfhSnapshot(raw: unknown): ResolvedMfhSnapshot {
  const r = asRecord(raw);
  const rawInput = asRecord(r.input);
  const rawPlayground = asRecord(r.playground);

  const mgmtMode: "gesamt" | "einheiten" = rawInput.mgmtMode === "einheiten" ? "einheiten" : "gesamt";
  const hasGesamtData = typeof rawInput.gesamtFlaecheM2 === "number" && typeof rawInput.kaltmieteJahr === "number";
  const hasUnitsData = Array.isArray(rawInput.units) && rawInput.units.length > 0;
  const complete =
    typeof rawInput.kaufpreis === "number" &&
    (mgmtMode === "einheiten" ? hasUnitsData : hasGesamtData);

  const input: MfhSnapshotInput = {
    mode: rawInput.mode === "erweitert" ? "erweitert" : "einfach",
    mgmtMode,
    gesamtFlaecheM2: resolveField(rawInput, "gesamtFlaecheM2", MFH_INPUT_DEFAULTS.gesamtFlaecheM2),
    kaltmieteJahr: resolveField(rawInput, "kaltmieteJahr", MFH_INPUT_DEFAULTS.kaltmieteJahr),
    units: resolveUnits(rawInput.units),
    nichtUmlagefaehigeKosten: resolveField(rawInput, "nichtUmlagefaehigeKosten", MFH_INPUT_DEFAULTS.nichtUmlagefaehigeKosten),
    leerstandPct: resolveField(rawInput, "leerstandPct", MFH_INPUT_DEFAULTS.leerstandPct),
    kaufpreis: resolveField(rawInput, "kaufpreis", MFH_INPUT_DEFAULTS.kaufpreis),
    nkRenovierung: resolveField(rawInput, "nkRenovierung", MFH_INPUT_DEFAULTS.nkRenovierung),
    nkSanierung: resolveField(rawInput, "nkSanierung", MFH_INPUT_DEFAULTS.nkSanierung),
    eigenkapital: resolveField(rawInput, "eigenkapital", MFH_INPUT_DEFAULTS.eigenkapital),
    manualLoan: resolveField(rawInput, "manualLoan", MFH_INPUT_DEFAULTS.manualLoan),
    darlehenManual: resolveField(rawInput, "darlehenManual", MFH_INPUT_DEFAULTS.darlehenManual),
    zins: resolveField(rawInput, "zins", MFH_INPUT_DEFAULTS.zins),
    tilgung: resolveField(rawInput, "tilgung", MFH_INPUT_DEFAULTS.tilgung),
    capexRuecklagePctBrutto: resolveField(rawInput, "capexRuecklagePctBrutto", MFH_INPUT_DEFAULTS.capexRuecklagePctBrutto),
    bewirtschaftungModus: rawInput.bewirtschaftungModus === "pauschal" || rawInput.bewirtschaftungModus === "einzeln" ? rawInput.bewirtschaftungModus : null,
    verwaltung: resolveField(rawInput, "verwaltung", MFH_INPUT_DEFAULTS.verwaltung),
    instandhaltungJeM2Jahr: resolveField(rawInput, "instandhaltungJeM2Jahr", MFH_INPUT_DEFAULTS.instandhaltungJeM2Jahr),
    baujahr: typeof rawInput.baujahr === "number" && Number.isFinite(rawInput.baujahr) ? rawInput.baujahr : null,
    hatAufzug: resolveField(rawInput, "hatAufzug", MFH_INPUT_DEFAULTS.hatAufzug),
    mietSteigerung: resolveField(rawInput, "mietSteigerung", MFH_INPUT_DEFAULTS.mietSteigerung),
    kostenSteigerung: resolveField(rawInput, "kostenSteigerung", MFH_INPUT_DEFAULTS.kostenSteigerung),
    vermietet:
      rawInput.vermietet === "teilweise" || rawInput.vermietet === "nein"
        ? rawInput.vermietet
        : "vollstaendig",
  };

  const playground: MfhSnapshotPlayground = {
    priceAdjPct: resolveField(rawPlayground, "priceAdjPct", resolveField(rawInput, "priceAdjPct", MFH_PLAYGROUND_DEFAULTS.priceAdjPct)),
    rentAdjPct: resolveField(rawPlayground, "rentAdjPct", resolveField(rawInput, "rentAdjPct", MFH_PLAYGROUND_DEFAULTS.rentAdjPct)),
    applyAdjustments: resolveField(rawPlayground, "applyAdjustments", resolveField(rawInput, "applyAdjustments", MFH_PLAYGROUND_DEFAULTS.applyAdjustments)),
  };

  const confirmedFields = resolveConfirmedFields(r, "confirmedFields");

  return { input, playground, complete, confirmedFields };
}
